import React from 'react'
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { grey } from '../../../../colors'

/**
 * Lowering the Course Section question limit below what sections already hold (CR-295).
 *
 * A sibling of `CapLoweringConflictModal` rather than a prop on it, because the payloads differ in
 * the field that modal is built around: a section-owned set has no single instructor, so the backend
 * deliberately sends no `instructor_user_id` / `instructor_name` here. Rendering this through the
 * instructor version produced "N instructors over the new limit" with a blank Instructor column, and
 * keyed every row on `undefined`.
 *
 * The counts are the same shape and the decision is the same, so the layout deliberately matches —
 * an admin who has lowered the instructor limit before should recognise this immediately.
 *
 * Spec: docs/specs/active/CR-295-section-added-questions/spec.md
 */
export interface SectionCapLoweringConflict {
  sectionId: string
  sectionTitle: string
  /** Section code. Optional: not every row carries one, and a title alone can be ambiguous. */
  sectionAbbreviation?: string | null
  existingQuestionCount: number
}

interface SectionCapLoweringConflictModalProps {
  conflicts: SectionCapLoweringConflict[]
  newQuestionCount?: number
  onCancel: () => void
  onConfirm: () => void
  isSubmitting?: boolean
}

const sectionNoun = (count: number): string => (count === 1 ? 'course section' : 'course sections')

export const SectionCapLoweringConflictModal: React.FC<SectionCapLoweringConflictModalProps> = ({
  conflicts,
  newQuestionCount,
  onCancel,
  onConfirm,
  isSubmitting = false
}) => (
  <>
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography fontWeight="bold" fontSize="0.875rem">
        {conflicts.length} {sectionNoun(conflicts.length)} already{' '}
        {conflicts.length === 1 ? 'holds' : 'hold'} more than{' '}
        {newQuestionCount ?? 'the new limit'} questions. They keep what they have — the new limit
        applies the next time their set is edited.
      </Typography>
    </Alert>

    <TableContainer sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Course Section</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Questions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conflicts.map((conflict) => (
            <TableRow key={conflict.sectionId}>
              <TableCell>
                {conflict.sectionTitle}
                {conflict.sectionAbbreviation != null && conflict.sectionAbbreviation !== '' && (
                  <Typography component="span" fontSize="13px" color={grey[850]} ml={1}>
                    {conflict.sectionAbbreviation}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{conflict.existingQuestionCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <DefaultModalActions
      destructiveButtonProps={{ onClick: onCancel, disabled: isSubmitting }}
      confirmButtonProps={{
        onClick: onConfirm,
        text: 'Set Limit',
        buttonType: ButtonType.Primary,
        loading: isSubmitting
      }}
    />
  </>
)
